import { api } from '@greenrinse/ui';

export interface PushStatus { enabled: boolean; publicKey: string | null; subscriptions: number }

/** Browser supports Web Push (Chrome/Edge/Firefox; Safari 16.4+ only when installed to the home screen). */
export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Gets the service-worker registration that will receive pushes. Apps without their own SW (admin, partner)
 * pass `swUrl` to register a tiny push-only worker; the operator PWA passes nothing and reuses its Workbox SW.
 */
async function registration(swUrl?: string): Promise<ServiceWorkerRegistration> {
  if (swUrl) {
    const reg = await navigator.serviceWorker.register(swUrl, { scope: import.meta.env.BASE_URL });
    await navigator.serviceWorker.ready;
    return reg;
  }
  return navigator.serviceWorker.ready;
}

/** Asks permission, subscribes this browser with the tenant's VAPID key and stores the subscription server-side. */
export async function subscribeToPush(swUrl?: string): Promise<'subscribed' | 'denied' | 'unsupported' | 'disabled'> {
  if (!pushSupported()) return 'unsupported';
  const status = await api<PushStatus>('/api/notifications/push');
  if (!status.enabled || !status.publicKey) return 'disabled';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const reg = await registration(swUrl);
  const key = urlBase64ToUint8Array(status.publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Re-subscribe if the server key changed (VAPID regenerated).
    const current = sub.options.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
    if (!current || current.length !== key.length || current.some((b, i) => b !== key[i])) { await sub.unsubscribe(); sub = null; }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const json = sub.toJSON();
  await api('/api/notifications/push/subscribe', { method: 'POST', body: { endpoint: json.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } } });
  try { localStorage.setItem('gr.push', '1'); } catch { /* ignore */ }
  return 'subscribed';
}

export async function unsubscribeFromPush(swUrl?: string): Promise<void> {
  if (!pushSupported()) return;
  const reg = await registration(swUrl);
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api('/api/notifications/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }).catch(() => undefined);
    await sub.unsubscribe();
  }
  try { localStorage.removeItem('gr.push'); } catch { /* ignore */ }
}

/** True when this browser has an active push subscription for the current site. */
export async function isSubscribed(swUrl?: string): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const reg = swUrl ? await navigator.serviceWorker.getRegistration('/') : await navigator.serviceWorker.ready;
    return !!(await reg?.pushManager.getSubscription());
  } catch { return false; }
}
